import React from 'react';
import { connect } from 'react-redux';
import * as reportsservice from '../../../services/reportsservice';
import BarChart from '../../reports/barChartReport/BarChartReport';
import DataSnapshot from '../../aylaTimeZone/AylaTimeZone';
import FilterPanel, {
  date_range, oem_id, oem_model, sw_version,
} from '../../filters/filterPanel/FilterPanel';
import ReportPage from '../ReportPage';
import TimeAggregation from '../../filters/timeAggregationFilter/TimeAggregationFilter';
import TitleBar from '../../titleBar/TitleBar';
import ValueDisplay from '../../reports/valueDisplayReport/ValueDisplayReport';
import { AYLA_ALLOWED_ROLES, GoogleAnalyticsConstants } from '../../../constants';
import './OverviewPage.css';
import { logPageViewEvent } from '../../../services/analyticsservice';
import {t} from "i18next";
import {getErrorReportNames, getReportData} from "../../../helpers/utils";

class OverviewPage extends ReportPage {

  googleAnalytics() {
    const { logged_in_oem_id } = this.props.presistFilters;
    logPageViewEvent(GoogleAnalyticsConstants.OverviewPage.title, logged_in_oem_id);
  }

  reports() {
    // Extract filters from props for easier access
    const filters = this.props.presistFilters;

    // Check if the region is 'US-FIELD' and OEM ID is in the list
    const isUsFieldDpMeteringOem = (this.props.presistFilters.region_name === 'US-FIELD' &&
        this.us_field_dp_metering_oems.includes(this.props.presistFilters.oem_id));

    // Check if the region is 'EU' and OEM ID is in the list
    const isEuFieldDpMeteringOem = (this.props.presistFilters.region_name === 'EU' &&
        this.eu_field_dp_metering_oems.includes(this.props.presistFilters.oem_id));

    // Check if the region is 'US-VPC' or 'EU' and OEM ID is '39a9391a'
    const isSharkVpc =
        (this.props.presistFilters.region_name === 'US-VPC' ||
            this.props.presistFilters.region_name === 'EU') &&
        this.props.presistFilters.oem_id === '39a9391a';

    // Create arrays for different types of reports
    const meteringReports = [
      reportsservice.contractTotalDatapointsTransactions(filters),
      reportsservice.contractTotalDatastreamTransactions(filters),
      reportsservice.contractSmsTransactions(filters),
      reportsservice.contractRuleEvaluationTransactions(filters),
      reportsservice.contractRuleExecutionTransactions(filters),
      reportsservice.contractDatapointsTransactionsOverTime(filters),
    ];

    const reportServiceReports = [
      reportsservice.activatedDevices(filters),
      reportsservice.devicesOnlineToday(filters),
      reportsservice.totalUsers(filters),
      reportsservice.deviceActivation(filters),
      reportsservice.usersWithRegisteredDevices(filters),
      reportsservice.totalDataTransmissionOverTime(filters),
      reportsservice.provisionedDevicesOverTime(filters),
      reportsservice.totalDevicesActivatedTillDate(filters),
      reportsservice.totalUsersActivatedTillDate(filters)
    ];

    return {
      filters: [
        reportsservice.listOfOemModels(filters),
        reportsservice.listOfSwVersions(filters),
        reportsservice.listOfOems(filters),
      ],
      reports: (isUsFieldDpMeteringOem || isEuFieldDpMeteringOem || isSharkVpc)
        ? meteringReports.concat(reportServiceReports)
          : [...reportServiceReports, reportsservice.totalDatapoints(filters),
            reportsservice.totalTransactions(filters)]
    };
  }

  reportsDescription = () => [
    reportsservice.deviceActivation(),
    reportsservice.usersWithRegisteredDevices(),
    reportsservice.totalDataTransmissionOverTime(),
  ];

  render() {
    const { fetchData, errors, session, isMeteringApi } = this.props;

    if(errors.length > 0) {
      getErrorReportNames(errors);
    }

    let deviceActivation = getReportData(
        this.report(reportsservice.deviceActivation),
        'New_Device_Activations_Over_Time'
    );

    let provisionedDevicesOverTime = getReportData(
        this.report(reportsservice.provisionedDevicesOverTime),
        'Provisioned_Devices_Over_Time'
    );

    const activatedAndPropvisionedOverTime =
      (deviceActivation === t('unableToFetchData') ||
        provisionedDevicesOverTime === t('unableToFetchData'))
          ? t('unableToFetchData')
          : (deviceActivation &&
            provisionedDevicesOverTime &&
            reportsservice.as3D('x', 'y', 'z')([
              ...deviceActivation[0].values.map(entry => ({
                z: 'activated',
                ...entry,
              })),
              ...provisionedDevicesOverTime[0].values.map(entry => ({
                z: 'provisioned',
                ...entry,
              })),
            ]));

    return (
      <div className="OverviewPage page">
        <TitleBar title="overview">
          <DataSnapshot snapShot={this.dataSnapshotTime} />
        </TitleBar>

        <div className="TopReports container-flex">
          <ValueDisplay
            title="devices.totalDevicesActivatedTillDate"
            data={{
              value: getReportData(
                this.report(reportsservice.totalDevicesActivatedTillDate),
                'Activated_Devices'
              ),
              description: t("devices.totalDevicesActivatedTillDateTooltip")
            }}
          />
          <ValueDisplay
            title="users.totalUsersRegisteredTillDate"
            data={{
              value: getReportData(
                this.report(reportsservice.totalUsersActivatedTillDate),
                'Total_Users'
              ),
              description: t("users.totalUsersRegisteredTillDateTooltip")
            }}
          />
          <ValueDisplay
            title="devices.onlineDevicesToday"
            data={{
              value: getReportData(
                this.report(reportsservice.devicesOnlineToday),
                'Devices_Online_Today'
              ),
              description: t("devices.onlineDevicesTodayTooltip")
            }}
          />
        </div>

        <div className="page-content-wrapper container-flex">
          <div className="pageContent">
            <div>
              <FilterPanel
                disabled={fetchData.fetches > 0}
                currentFilters={this.props.presistFilters}
                filters={[
                  date_range,
                  AYLA_ALLOWED_ROLES.includes(session && session.role) && oem_id(this.report(reportsservice.listOfOems)),
                  oem_model(this.report(reportsservice.listOfOemModels)),
                  sw_version(this.report(reportsservice.listOfSwVersions))
                ]}
                hideDefaultReports
              />
              <TimeAggregation value={this.props.presistFilters.aggregate_by} />
            </div>
            <div className="container-flex">
              <ValueDisplay
                title="devices.newActivatedDevices"
                data={{
                  value: getReportData(
                    this.report(reportsservice.activatedDevices),
                    'Activated_Devices'
                  ),
                  description: t("devices.newActivatedDevicesTooltip")
                }}
              />
              <ValueDisplay
                title="users.newRegisteredUsers"
                data={{
                  value: getReportData(
                    this.report(reportsservice.totalUsers),
                    'Total_Users'
                  ),
                  description: t("users.newRegisteredUsersTooltip")
                }}
              />
              {! isMeteringApi && (
                <>
                  <ValueDisplay
                    id="totalTransactions"
                    title="meteringPage.transactions.totalTransactions"
                    data={{
                      value: getReportData(
                        this.report(reportsservice.totalTransactions),
                        'Contract_Total_Transactions'
                      ),
                      description: t("meteringPage.toolTip.totalTransactions")
                    }}
                  />
                  <ValueDisplay
                    id="datapointTransmissions"
                    title="datapoints.total"
                    data={{
                      value: getReportData(
                        this.report(reportsservice.totalDatapoints),
                        'Datapoint_Transmissions'
                      ),
                      description: t("meteringPage.toolTip.datapointTransactions")
                    }}
                  />
                </>
              )}
              {isMeteringApi && (
                <>
                  <ValueDisplay
                    id="totalTransactions"
                    title="meteringPage.transactions.totalTransactions"
                    data={{
                      value: this.getTotalTransactions(
                        this.report(
                          reportsservice.contractTotalDatapointsTransactions
                        ),
                        this.report(
                          reportsservice.contractTotalDatastreamTransactions
                        ),
                        this.report(
                          reportsservice.contractSmsTransactions
                        ),
                        this.report(
                          reportsservice.contractRuleEvaluationTransactions
                        ),
                        this.report(
                          reportsservice.contractRuleExecutionTransactions
                        )
                      ),
                      description: t("meteringPage.toolTip.totalTransactions")
                    }}
                  />
                  <ValueDisplay
                    id="datapointTransmissions"
                    title="datapoints.total"
                    data={{
                      value:
                        this.report(reportsservice.contractTotalDatapointsTransactions) &&
                        this.report(reportsservice.contractTotalDatapointsTransactions).hasOwnProperty('message')
                          ? t('unableToFetchData')
                          : this.report(reportsservice.contractTotalDatapointsTransactions) &&
                            this.report(reportsservice.contractTotalDatapointsTransactions).total_datapoint
                            ? this.report(reportsservice.contractTotalDatapointsTransactions).total_datapoint
                            : null,
                      description: t("meteringPage.toolTip.datapointTransactions")
                    }}
                  />
                </>
              )}
            </div>
            <div>
              <BarChart
                title="devices.activations"
                width={this.widthForColumns(1)}
                height={200}
                data={activatedAndPropvisionedOverTime}
                description={this.reportDescription(reportsservice.deviceActivation)}
                xLabel={this.props.presistFilters.aggregate_by}
                yLabel="devices"
                grouped
              />
            </div>
            <div>
              <BarChart
                title="users.withRegisteredDevices"
                width={this.widthForColumns(2)}
                height={200}
                data={getReportData(
                  this.report(reportsservice.usersWithRegisteredDevices),
                  'New_User_Registration_Over_Time'
                )}
                description={this.reportDescription(reportsservice.usersWithRegisteredDevices)}
                xLabel={this.props.presistFilters.aggregate_by}
                yLabel="users"
              />
              {!isMeteringApi && (
                <BarChart
                  title="devices.countByStatus"
                  width={this.widthForColumns(2)}
                  height={200}
                  data={getReportData(
                    this.report(reportsservice.totalDataTransmissionOverTime),
                    'Total_Data_Transmission_Over_Time'
                  )}
                  description={this.reportDescription(reportsservice.totalDataTransmissionOverTime)}
                  xLabel={this.props.presistFilters.aggregate_by}
                  yLabel="transmissions"
                />
              )}
              {isMeteringApi && (
                <BarChart
                  title="devices.countByStatus"
                  width={this.widthForColumns(2)}
                  height={200}
                  data={getReportData(
                    this.report(reportsservice.contractDatapointsTransactionsOverTime),
                    'Contract_Datapoint_Transactions_Over_Time'
                  )}
                  description={this.reportDescription(reportsservice.totalDataTransmissionOverTime)}
                  xLabel={this.props.presistFilters.aggregate_by}
                  yLabel="transmissions"
                  grouped
                />
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default connect(state => state)(OverviewPage);
